import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, StyleSheet, View } from 'react-native';

import { EmptyState, SkeletonRow } from '~/components/core';
import AIStrategyBuilderService from '~/api-services/AIStrategyBuilderService';
import { StrategyExecution } from '~/api-services/AIStrategyBuilderService/types';
import { scaleSize } from '~/constants/scale';
import { useStyles } from '~/hooks';
import { Theme } from '~/theme';

import { useStrategyPaged } from '../../hooks/useStrategyPaged';
import RunRow from './components/RunRow';

type Props = {
    strategyId: string;
};

type Run = {
    key: string;
    code: string;
    items: StrategyExecution[];
};

const foldKey = (item: StrategyExecution): string => `${item.code}:${String(item.context?.errorCode ?? '')}`;

// The cron records every evaluation, so months of "conditions were not met" bury the few rows
// that matter. Consecutive same-outcome entries fold into one; a row naming a position never
// folds, since it is about one specific trade.
const groupExecutions = (items: StrategyExecution[]): Run[] => {
    const runs: Run[] = [];
    for (const item of items) {
        const last = runs[runs.length - 1];
        const foldable = !item.positionHumanId;
        if (last && foldable && !last.items[0].positionHumanId && foldKey(last.items[0]) === foldKey(item)) {
            last.items.push(item);
            continue;
        }
        runs.push({ key: `${item.code}-${item.created}`, code: item.code, items: [item] });
    }
    return runs;
};

export default function ActivityList({ strategyId }: Props) {
    const styles = useStyles(stylesGenerator);
    const [expanded, setExpanded] = useState<string[]>([]);

    const fetchExecutions = useCallback(
        (params: { page: number; size: number }) => AIStrategyBuilderService.getStrategyExecutions(strategyId, params),
        [strategyId]
    );
    const { items, loading, loadingMore, error, refresh, loadMore } =
        useStrategyPaged<StrategyExecution>(fetchExecutions);

    const runs = useMemo(() => groupExecutions(items), [items]);

    const toggle = (key: string) =>
        setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

    if (loading && items.length === 0) {
        return (
            <View style={styles.skeletons}>
                <SkeletonRow height={20} width="100%" />
                <SkeletonRow height={20} width="100%" />
                <SkeletonRow height={20} width="100%" />
            </View>
        );
    }

    if (items.length === 0) {
        return error ? (
            <EmptyState title="ai-strategy-activity-empty" body={error} ctaLabel="retry" onCtaPress={refresh} />
        ) : (
            <EmptyState title="ai-strategy-activity-empty" body="ai-strategy-activity-empty-body" />
        );
    }

    return (
        <FlatList
            style={styles.container}
            contentContainerStyle={styles.content}
            data={runs}
            keyExtractor={(run) => run.key}
            initialNumToRender={20}
            onEndReachedThreshold={0.4}
            onEndReached={loadMore}
            renderItem={({ item: run }) => (
                <RunRow
                    items={run.items}
                    code={run.code}
                    expanded={expanded.includes(run.key)}
                    onToggle={() => toggle(run.key)}
                />
            )}
            ListFooterComponent={
                loadingMore ? (
                    <View style={styles.footer}>
                        <ActivityIndicator />
                    </View>
                ) : null
            }
        />
    );
}

const stylesGenerator = (_theme: Theme) =>
    StyleSheet.create({
        container: { flex: 1 },
        content: { paddingHorizontal: scaleSize(12), paddingBottom: scaleSize(12) },
        skeletons: { padding: scaleSize(12) },
        footer: { paddingVertical: scaleSize(12) },
    });
